import React from 'react';
import { MountainIcon, SearchIcon, UserIcon, PlusIcon, MicIcon, ArrowUpIcon, FileTextIcon, AlertTriangleIcon, ReceiptIcon, CalendarDaysIcon, HomeIcon, FolderIcon, BuildingIcon, UsersIcon, BellIcon, CreditCardIcon, ChevronRightIcon, LayoutGridIcon, MapIcon, QrCodeIcon, ArrowRightIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { DX_IMAGES, dxUi, type Bi } from '../../data/digitalExperienceHero';
const WEB_ICONS = {
  permit: FileTextIcon,
  report: AlertTriangleIcon,
  bill: ReceiptIcon,
  leisure: CalendarDaysIcon
} as const;
const PORTAL_ICONS = {
  home: HomeIcon,
  requests: FolderIcon,
  properties: BuildingIcon,
  family: UsersIcon,
  activities: CalendarDaysIcon,
  notifications: BellIcon,
  bills: ReceiptIcon,
  card: CreditCardIcon,
  profile: UserIcon
} as const;
const PHONE_ICONS = {
  report: BellIcon,
  permit: FileTextIcon,
  card: LayoutGridIcon,
  leisure: CalendarDaysIcon
} as const;
const TAB_ICONS = {
  home: HomeIcon,
  services: LayoutGridIcon,
  card: MapIcon,
  profile: UserIcon
} as const;
function usePick() {
  const {
    lang
  } = useLanguage();
  return (b: Bi) => lang === 'FR' ? b.fr : b.en;
}
function Brand({
  size = 'md',
  light = false
}: {
  size?: 'sm' | 'md';
  light?: boolean;
}) {
  return <span className={`inline-flex items-center gap-1.5 font-sans font-semibold ${light ? 'text-white' : 'text-[#121A3A]'} ${size === 'sm' ? 'text-[12px]' : 'text-[14px]'}`} data-id="element-3130" data-mp="2agh1ga2j2pnp">
      <MountainIcon className={`${size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'} text-[#4F46E5]`} strokeWidth={2} aria-hidden="true" data-id="element-3131" data-mp="2kf6jq3yc6c4h" />
      {dxUi.brand}
    </span>;
}
function AskField({
  compact = false
}: {
  compact?: boolean;
}) {
  const pick = usePick();
  return <div className={`flex items-center gap-2 rounded-full bg-white shadow-[0_8px_24px_-12px_rgba(18,26,58,0.5)] ${compact ? 'h-8 pl-3 pr-1' : 'h-11 pl-4 pr-1.5'}`} data-id="element-3132" data-mp="2y7mnj6z2ae6g">
      <PlusIcon className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} text-[#121A3A]`} aria-hidden="true" data-id="element-3133" data-mp="338k5big5t67f" />
      <span className={`flex-1 font-sans text-[#6B7180] truncate ${compact ? 'text-[9px]' : 'text-[12px]'}`} data-id="element-3134" data-mp="38625ajc78b3a">{pick(dxUi.ask)}</span>
      {!compact && <MicIcon className="w-4 h-4 text-[#6B7180]" aria-hidden="true" data-id="element-3135" data-mp="2dj3n6ogbao44" />}
      <span className={`rounded-full bg-[#4F46E5] flex items-center justify-center ${compact ? 'w-6 h-6' : 'w-8 h-8'}`} data-id="element-3136" data-mp="2dgqxfp4bb9gi">
        <ArrowUpIcon className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} text-white`} strokeWidth={2.4} aria-hidden="true" data-id="element-3137" data-mp="2nh17x1yyq2rz" />
      </span>
    </div>;
}

// Simple deterministic QR-like pattern for the citizen card.
function QrPattern({
  size = 11,
  cell = 4
}: {
  size?: number;
  cell?: number;
}) {
  const cells: boolean[] = [];
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const finder = x < 3 && y < 3 || x > size - 4 && y < 3 || x < 3 && y > size - 4;
      cells.push(finder || (x * 7 + y * 13 + x * y) % 3 === 0);
    }
  }
  return <div className="grid" style={{
    gridTemplateColumns: `repeat(${size}, ${cell}px)`
  }} aria-hidden="true" data-id="element-3138" data-mp="1guf9g4bmpn0i">
      {cells.map((on, i) => <span key={i} style={{
      width: cell,
      height: cell
    }} className={on ? 'bg-[#121A3A]' : 'bg-white'} data-id="element-3139" data-mp="3ngtjomjwqcxs" data-mpi={i} />)}
    </div>;
}
export function WebsiteLaptop() {
  const pick = usePick();
  return <div className="w-[580px]" data-id="element-3140" data-mp="3dr1opr7wbhss">
      <div className="rounded-[22px] bg-[#1C1F2A] p-[10px] shadow-[0_40px_70px_-30px_rgba(18,26,58,0.55)]" data-id="element-3141" data-mp="3dpqp83lhgw7h">
        <div className="rounded-[12px] overflow-hidden bg-white h-[378px] flex flex-col" data-id="element-3142" data-mp="3iojzln5h473f">
          <div className="h-[42px] shrink-0 flex items-center gap-4 px-4 border-b border-[#EEF0F4]" data-id="element-3143" data-mp="34p0xbazcfpz1">
            <Brand data-id="element-3144" data-mp="38sgp9u0gx25e" />
            <nav className="flex-1 flex items-center justify-end gap-3" aria-hidden="true" data-id="element-3145" data-mp="38t095g9oi0m2">
              {dxUi.nav.map(n => <span key={n.fr} className="font-sans text-[8.5px] text-[#121A3A] whitespace-nowrap" data-id="element-3146" data-mp="2uu0g7wf4uzk3">{pick(n)}</span>)}
            </nav>
            <SearchIcon className="w-3.5 h-3.5 text-[#121A3A]" aria-hidden="true" data-id="element-3147" data-mp="t01adqks5aes" />
            <span className="inline-flex items-center gap-1 rounded-full bg-[#121A3A] px-2.5 py-1 font-sans text-[8.5px] font-medium text-white whitespace-nowrap" data-id="element-3148" data-mp="xzmaw5fllqtm">
              <UserIcon className="w-2.5 h-2.5" aria-hidden="true" data-id="element-3149" data-mp="2u1zcyrzpf5lt" />
              {pick(dxUi.mySpace)}
            </span>
          </div>
          <div className="relative h-[228px] shrink-0" data-id="element-3150" data-mp="2l1u5khuig77n">
            <img src={DX_IMAGES.town} alt="" className="absolute inset-0 w-full h-full object-cover" data-id="element-3151" data-mp="2l09betc3yqab" />
            <div className="absolute inset-0 bg-[#0E1330]/30" aria-hidden="true" data-id="element-3152" data-mp="2g2pz3yyz0nl0" />
            <div className="relative px-5 pt-7" data-id="element-3153" data-mp="2b4ocx569085b">
              <p className="font-serif text-[27px] leading-[1.05] text-white max-w-[330px] [text-shadow:0_2px_10px_rgba(0,0,0,0.35)]" data-id="element-3154" data-mp="26dg5ufse4yvb">{pick(dxUi.question)}</p>
              <div className="mt-4 max-w-[400px]" data-id="element-3155" data-mp="21ecz8qwrikl8"><AskField data-id="element-3156" data-mp="1xci1txhycrcq" /></div>
            </div>
            <div className="absolute left-4 right-4 -bottom-9 grid grid-cols-4 gap-2.5" data-id="element-3157" data-mp="1xdt0jiwjj8wz">
              {dxUi.webActions.map(a => {
              const Icon = WEB_ICONS[a.key];
              return <div key={a.key} className="rounded-lg bg-white border border-[#EEF0F4] shadow-[0_8px_20px_-14px_rgba(18,26,58,0.5)] px-3 py-2.5 h-[62px]" data-id="element-3158" data-mp="1neu36wh86zpa">
                    <Icon className="w-3.5 h-3.5 text-[#4F46E5]" strokeWidth={1.8} aria-hidden="true" data-id="element-3159" data-mp="7uut3ce7bg4r" />
                    <p className="mt-1.5 font-sans text-[9px] leading-tight text-[#121A3A]" data-id="element-3160" data-mp="cu7gpzcwcr8e">{pick(a.label)}</p>
                  </div>;
            })}
            </div>
          </div>
          <div className="px-4 pt-12" data-id="element-3161" data-mp="3gbz38y89kiz7">
            <p className="font-sans text-[11px] font-semibold text-[#121A3A]" data-id="element-3162" data-mp="3gd0r63adozyc">{pick(dxUi.news)}</p>
            <div className="mt-2 grid grid-cols-3 gap-2.5" data-id="element-3163" data-mp="3lair546f44u7">
              {[0, 1, 2].map(i => <img key={i} src={i === 1 ? DX_IMAGES.news : DX_IMAGES.town} alt="" className="h-[40px] w-full rounded-md object-cover" style={{
              objectPosition: `${i * 40}% 50%`
            }} data-id="element-3164" data-mp="3pakt7eg2f0a0" />)}
            </div>
          </div>
        </div>
      </div>
      <div className="relative mx-[-34px] h-[16px] rounded-b-[14px] bg-[#C9CBD3] shadow-[0_18px_30px_-16px_rgba(18,26,58,0.55)]" data-id="element-3165" data-mp="31lrx9hzg0sfe">
        <span className="absolute left-1/2 -translate-x-1/2 top-0 w-24 h-[6px] rounded-b-md bg-[#AEB1BB]" aria-hidden="true" data-id="element-3166" data-mp="8q8fu4tz871q" />
      </div>
    </div>;
}
export function PortalScreen({
  stats = dxUi.stats
}: {
  stats?: {
    value: string;
    label: Bi;
  }[];
}) {
  const pick = usePick();
  return <div className="w-[440px] rounded-[18px] bg-[#1C1F2A] p-[8px] shadow-[0_30px_60px_-30px_rgba(18,26,58,0.5)]" data-id="element-3167" data-mp="djuir5ac8wkc">
      <div className="rounded-[11px] bg-white h-[400px] overflow-hidden" data-id="element-3168" data-mp="3lyuz5207ugs8">
        <div className="h-[44px] flex items-center justify-between px-4 border-b border-[#EEF0F4]" data-id="element-3169" data-mp="3h0a1naccmb7h">
          <span className="inline-flex items-center gap-1.5" data-id="element-3170" data-mp="3v02lclpgjr5y">
            <MountainIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-3171" data-mp="3qxfuan0m2a9t" />
            <span className="font-sans text-[14px] font-semibold text-[#121A3A]" data-id="element-3172" data-mp="1a8shszp4fgnb">{pick(dxUi.mySpace)}</span>
          </span>
          <img src={DX_IMAGES.avatar} alt="" className="w-7 h-7 rounded-full object-cover" data-id="element-3173" data-mp="167yr5ykannqq" />
        </div>
        <div className="flex h-[356px]" data-id="element-3174" data-mp="1dpojhyzbl7cy">
          <ul className="w-[118px] shrink-0 border-r border-[#EEF0F4] py-2.5 px-2 space-y-1" data-id="element-3175" data-mp="1dssv3hf475dn">
            {dxUi.portalNav.map((n, i) => {
            const Icon = PORTAL_ICONS[n.key];
            return <li key={n.key} className={`flex items-center gap-1.5 rounded-md px-2 py-[6px] ${i === 0 ? 'bg-[#F1F0FD] text-[#4F46E5]' : 'text-[#121A3A]'}`} data-id="element-3176" data-mp="13uwmoxdkaoj8" data-mpi={i}>
                  <Icon className="w-3 h-3 shrink-0 text-[#4F46E5]" strokeWidth={1.8} aria-hidden="true" data-id="element-3177" data-mp="1wiirg8yizz7o" />
                  <span className="font-sans text-[8.5px] truncate" data-id="element-3178" data-mp="21frascveu9ai">{pick(n.label)}</span>
                </li>;
          })}
          </ul>
          <div className="flex-1 min-w-0 p-3.5" data-id="element-3179" data-mp="1rpzbj5cd8rzp">
            <p className="font-serif text-[19px] text-[#121A3A]" data-id="element-3180" data-mp="1roo8j7qqvo9n">{pick(dxUi.hello)}</p>
            <p className="font-sans text-[8px] text-[#8A90A2] mt-0.5" data-id="element-3181" data-mp="2ffa6u7ay5u6g">{pick(dxUi.helloSub)}</p>
            <div className="mt-2.5 grid grid-cols-2 gap-2" data-id="element-3182" data-mp="2k7k11zj3hgej">
              {stats.map(s => <div key={s.value + s.label.fr} className="rounded-lg border border-[#EEF0F4] px-2.5 py-2" data-id="element-3183" data-mp="k5c6ie8k0rt9">
                  <p className="font-sans text-[15px] font-semibold text-[#121A3A]" data-id="element-3184" data-mp="fc9a7yce749d">{s.value}</p>
                  <p className="font-sans text-[8px] text-[#6B7180] leading-tight" data-id="element-3185" data-mp="bcqo4z8u4n4y">{pick(s.label)}</p>
                  <p className="mt-1 inline-flex items-center gap-0.5 font-sans text-[7.5px] text-[#4F46E5]" data-id="element-3186" data-mp="6ep7sm2sn1x6">{pick(dxUi.seeAll)}<ArrowRightIcon className="w-2 h-2" aria-hidden="true" data-id="element-3187" data-mp="132lodqy8dx8e" /></p>
                </div>)}
            </div>
            <div className="mt-2.5 rounded-lg border border-[#EEF0F4] p-2.5 flex items-center gap-3" data-id="element-3188" data-mp="u2qcfjwupy7b">
              <div data-id="element-3189" data-mp="p3vs2dj8k1l3">
                <p className="font-sans text-[9px] font-semibold text-[#121A3A]" data-id="element-3190" data-mp="p4exwvvm2zir">{pick(dxUi.citizenCard)}</p>
                <p className="mt-2 inline-flex items-center gap-1 font-sans text-[8px] text-[#4F46E5]" data-id="element-3191" data-mp="3etz2h94ct4ux">
                  <QrCodeIcon className="w-2.5 h-2.5" aria-hidden="true" data-id="element-3192" data-mp="39t31b3bdzwt7" />{pick(dxUi.showCard)}
                </p>
              </div>
              <div className="ml-auto" data-id="element-3193" data-mp="2zntdx6r4jm67"><QrPattern data-id="element-3194" data-mp="34or528u43qju" /></div>
            </div>
            <p className="mt-3 font-sans text-[9px] font-semibold text-[#121A3A]" data-id="element-3195" data-mp="34pioymecjzua">{pick(dxUi.upcoming)}</p>
            <ul className="mt-1.5 space-y-1.5" data-id="element-3196" data-mp="39mit2rrmysir">
              {dxUi.activities.map(a => <li key={a.title.fr} className="flex items-center gap-2 rounded-lg border border-[#EEF0F4] px-2 py-1.5" data-id="element-3197" data-mp="3egdafojrqveo">
                  <span className="w-5 h-5 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" data-id="element-3198" data-mp="3iii6sv1lq650">
                    <CalendarDaysIcon className="w-2.5 h-2.5 text-[#4F46E5]" aria-hidden="true" data-id="element-3199" data-mp="3selgclys971z" />
                  </span>
                  <span className="min-w-0" data-id="element-3200" data-mp="18ts5s4kg4ht">
                    <span className="block font-sans text-[8.5px] font-medium text-[#121A3A]" data-id="element-3201" data-mp="17ip5uiy30rr">{pick(a.title)}</span>
                    <span className="block font-sans text-[7.5px] text-[#8A90A2]" data-id="element-3202" data-mp="1y2wyaax98odd">{pick(a.when)}</span>
                  </span>
                  <ChevronRightIcon className="w-3 h-3 text-[#B4B8C5] ml-auto" aria-hidden="true" data-id="element-3203" data-mp="2m5wc58ri0w29" />
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </div>;
}
export function AppPhone() {
  const pick = usePick();
  return <div className="w-[196px] rounded-[34px] bg-[#1C1F2A] p-[7px] shadow-[0_34px_60px_-24px_rgba(18,26,58,0.6)]" data-id="element-3204" data-mp="2v8nnl8kdjrtw">
      <div className="relative rounded-[28px] bg-[#FAFAFD] h-[410px] overflow-hidden flex flex-col" data-id="element-3205" data-mp="354iaifvms92h">
        <span className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-[18px] rounded-full bg-[#1C1F2A] z-10" aria-hidden="true" data-id="element-3206" data-mp="3a3bpyduhaa5a" />
        <div className="pt-8 px-3 pb-2 flex items-center justify-between" data-id="element-3207" data-mp="3evczhs09hmrm">
          <Brand size="sm" data-id="element-3208" data-mp="3iwez49y3c0u4" />
          <BellIcon className="w-3.5 h-3.5 text-[#121A3A]" aria-hidden="true" data-id="element-3209" data-mp="3iulrarbqrakj" />
        </div>
        <div className="relative h-[150px] shrink-0" data-id="element-3210" data-mp="3nw1bkh63hd1v">
          <img src={DX_IMAGES.town} alt="" className="absolute inset-0 w-full h-full object-cover" style={{
          objectPosition: '38% 50%'
        }} data-id="element-3211" data-mp="3su2un23itcdc" />
          <div className="absolute inset-0 bg-[#0E1330]/30" aria-hidden="true" data-id="element-3212" data-mp="6tndic3rtv76" />
          <p className="relative px-3 pt-4 font-serif text-[17px] leading-[1.05] text-white text-center [text-shadow:0_2px_8px_rgba(0,0,0,0.35)]" data-id="element-3213" data-mp="1szq00er3whp">{pick(dxUi.question)}</p>
          <div className="absolute left-2.5 right-2.5 bottom-3" data-id="element-3214" data-mp="3t1n9sbxcfbn5"><AskField compact data-id="element-3215" data-mp="3oza39larv9pb" /></div>
        </div>
        <div className="grid grid-cols-2 gap-2 px-2.5 pt-2.5" data-id="element-3216" data-mp="3p0l5vhshebeu">
          {dxUi.phoneActions.map(a => {
          const Icon = PHONE_ICONS[a.key];
          return <div key={a.key} className="rounded-xl bg-white border border-[#EEF0F4] h-[62px] flex flex-col items-center justify-center gap-1 px-1" data-id="element-3217" data-mp="kksg9n2fr3lq">
                <Icon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.8} aria-hidden="true" data-id="element-3218" data-mp="bs8ggjaeqszb" />
                <span className="font-sans text-[8px] leading-tight text-center text-[#121A3A]" data-id="element-3219" data-mp="18a4q8p5l7fp3">{pick(a.label)}</span>
              </div>;
        })}
        </div>
        <div className="mt-auto h-[46px] border-t border-[#EEF0F4] bg-white grid grid-cols-4" data-id="element-3220" data-mp="13h1v4cl5vy6s">
          {dxUi.tabs.map((t, i) => {
          const Icon = TAB_ICONS[t.key];
          return <span key={t.key} className={`flex flex-col items-center justify-center gap-0.5 ${i === 0 ? 'text-[#121A3A]' : 'text-[#8A90A2]'}`} data-id="element-3221" data-mp="2o0x5plxocr24" data-mpi={i}>
                <Icon className="w-3.5 h-3.5" strokeWidth={i === 0 ? 2.2 : 1.8} aria-hidden="true" data-id="element-3222" data-mp="2syyr4dikp2gr" />
                <span className="font-sans text-[7px]" data-id="element-3223" data-mp="2ezfsqcfkd5hn">{pick(t.label)}</span>
              </span>;
        })}
        </div>
      </div>
    </div>;
}